/* Turns a YouTube / Vimeo share URL into a privacy-friendly embeddable iframe.
   Falls back to an elegant dark panel when the URL cannot be embedded. */
import { useTranslation } from "react-i18next";
import { Radio } from "lucide-react";

export const toEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1` : null;
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch" && u.searchParams.get("v")) {
        return `https://www.youtube.com/embed/${u.searchParams.get("v")}?autoplay=1&mute=1&playsinline=1`;
      }
      if (u.pathname.startsWith("/live/") || u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/")[2];
        return id ? `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1` : null;
      }
      if (u.pathname.startsWith("/embed/")) return url;
    }
    if (host === "player.vimeo.com") return url;
    if (host.endsWith("vimeo.com")) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (/^\d+$/.test(id || "")) return `https://player.vimeo.com/video/${id}?autoplay=1&muted=1`;
    }
  } catch {
    return null;
  }
  return null;
};

export const StreamEmbed = ({ url, className = "", testid = "stream-embed" }) => {
  const { t } = useTranslation();
  const embed = toEmbedUrl(url);

  if (!url) return null;

  if (!embed) {
    return (
      <div
        data-testid={`${testid}-fallback`}
        className={`aspect-video rounded-2xl bg-[#0A0A0A] flex flex-col items-center justify-center gap-3 text-center px-6 ${className}`}
      >
        <Radio className="w-6 h-6 text-white/40" aria-hidden="true" />
        <p className="text-sm text-white/60">{t("live.streamNotEmbeddable")}</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-open-link`}
          className="text-sm font-medium text-white underline underline-offset-4 hover:text-white/80 transition-colors duration-200"
        >
          {t("live.openStream")}
        </a>
      </div>
    );
  }

  return (
    <div data-testid={testid} className={`aspect-video rounded-2xl overflow-hidden bg-[#0A0A0A] ${className}`}>
      <iframe
        src={embed}
        title={t("live.streamTitle")}
        className="w-full h-full"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
    </div>
  );
};
